import React, { useEffect, useMemo, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';

import {
    Box,
    CircularProgress,
    Button
} from '@mui/material';

import {
    DataGridPro,
    GridToolbar,
    GridColDef,
    GridRowId,
} from '@mui/x-data-grid-pro';

import { useOutletContext } from 'react-router-dom';
import WarningRoundedIcon from '@mui/icons-material/WarningRounded';
import { toast } from 'react-toastify';
import axios from 'axios';

import { socket } from '../services/socket';

interface VendorRow {
    id: GridRowId;
    col0: number;
    col1: string;
    col2: string;
    col3: string;
    col4: string;
    col5: string;
    col6: number | null;
    col7: number | null;
    col8: number;
    col9: boolean;
    col10?: unknown;
}

interface OutletContextType {
    isConnected: boolean;
    maestroPermissions: string[];
}

interface EditedRowMap {
    [key: string]: VendorRow;
}

const api = axios.create({
    withCredentials: true
});

const Vendors: React.FC = () => {
    const { isConnected, maestroPermissions } =
        useOutletContext<OutletContextType>();

    const { getAccessTokenSilently } = useAuth0();

    const [vendorRows, setVendorRows] = useState<VendorRow[]>([]);
    const [editedVendorRows, setEditedVendorRows] = useState<EditedRowMap>({});
    const [originalVendorRows, setOriginalVendorRows] = useState<VendorRow[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(false);

    const isAuthorized = maestroPermissions.includes(`${process.env.REACT_APP_TENANT}:RevProVendor:Read`) &&
        maestroPermissions.includes(`${process.env.REACT_APP_TENANT}:RevProVendor:Commissions:Update`)

    const isVendorRowDirty = (rowId: GridRowId) => {
        return !!editedVendorRows[String(rowId)];
    };

    function formatPercentDisplay(v: number | null | undefined) {
        if (v == null) return undefined;
        const num = v * 100;
        return Number.isInteger(num) ? num : Number(num.toFixed(2));
    }

    const handleProcessVendorRowUpdate = (newRow: VendorRow): VendorRow => {
        const normalizeNumber = (v: any) => {
            const n = parseFloat(v);
            return isNaN(n) ? null : n;
        };

        const normalizedRow: VendorRow = {
            ...newRow,
            col6: normalizeNumber(newRow.col6),
            col7: normalizeNumber(newRow.col7)
        };

        setVendorRows((prev) =>
            prev.map((r) => (r.id === newRow.id ? normalizedRow : r))
        );

        const original = originalVendorRows.find((r) => r.id === newRow.id);

        const isDirty =
            original?.col6 !== normalizedRow.col6 ||
            original?.col7 !== normalizedRow.col7 ||
            original?.col8 !== normalizedRow.col8 ||
            original?.col9 !== normalizedRow.col9;

        setEditedVendorRows((prev) => {
            const copy = { ...prev };

            if (isDirty) {
                copy[String(newRow.id)] = normalizedRow;
            } else {
                delete copy[String(newRow.id)];
            }

            return copy;
        });

        return normalizedRow;
    };

    const handleClearVendorRow = (rowId: GridRowId) => {
        const originalRow = originalVendorRows.find((r) => r.id === rowId);
        if (!originalRow) return;

        setVendorRows((prev) =>
            prev.map((r) => (r.id === rowId ? { ...originalRow } : r))
        );

        setEditedVendorRows((prev) => {
            const copy = { ...prev };
            delete copy[String(rowId)];
            return copy;
        });
    };

    async function handleSaveVendorUpdates(vendorId: GridRowId, row: VendorRow) {
        try {
            setIsLoading(true);

            const audience = process.env.REACT_APP_AUTH0AUDIENCE_MAESTRO;

            const token = await getAccessTokenSilently({
                authorizationParams: { audience }
            });

            const convertToPercentDecimal = (v: any) => {
                const n = parseFloat(v);
                if (isNaN(n)) return null;
                return n / 100;
            };

            const payload = {
                commissionsPercentageOverRevenue: convertToPercentDecimal(row.col6),
                commissionsPercentageOverProfit: convertToPercentDecimal(row.col7),
                commissionsPayOn: row.col8,
                commissionsShouldCalculate: row.col9
            };

            await api.patch(
                `${process.env.REACT_APP_MAESTRO_DOMAIN}/revProVendors/${vendorId}/commissions`,
                payload,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'x-tenant': process.env.REACT_APP_TENANT
                    }
                }
            );

            toast.success('Vendor details updated.');

            setEditedVendorRows((prev) => {
                const copy = { ...prev };
                delete copy[String(vendorId)];
                return copy;
            });

            setOriginalVendorRows((prev) =>
                prev.map((r) =>
                    r.id === vendorId ? { ...r, ...row } : r
                )
            );
        } catch (err: any) {
            toast.error(
                err?.response?.data?.message ||
                    'Failed to update vendor details.'
            );
        } finally {
            setIsLoading(false);
        }
    }

    async function getVendorData() {
        try {
            setIsLoading(true);

            const audience = process.env.REACT_APP_AUTH0AUDIENCE_MAESTRO;

            const token = await getAccessTokenSilently({
                authorizationParams: { audience },
            });

            const response = await api.get(
                `${process.env.REACT_APP_MAESTRO_DOMAIN}/revProVendors/`,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'x-tenant': process.env.REACT_APP_TENANT
                    }
                }
            );

            const r: VendorRow[] = response.data.map((row: any) => {
                const tipaltiErrors = row.tipaltiNonPayableReasons ?? [];

                return {
                id: row.vendorId,
                col0: row.tnClientBrokerId,
                col1: row.tipaltiIsPayable,
                col2: tipaltiErrors.join(", "),
                col3: row.companyName,
                col4: row.email,
                col5: row.division,
                col6: formatPercentDisplay(
                    row.commissionsPercentageOverRevenue
                ),
                col7: formatPercentDisplay(
                    row.commissionsPercentageOverProfit
                ),
                col8: row.commissionsPayOn,
                col9: row.commissionsShouldCalculate
            }});

            setVendorRows(r);
            setOriginalVendorRows(structuredClone(r));
            setEditedVendorRows({});
        } catch (err: any) {
            if (err.message !== 'canceled') {
                toast.error(
                    err?.response?.data?.message || 'Request failed'
                );
            }
        } finally {
            setIsLoading(false);
        }
    }

    useEffect(() => {
        if (isConnected && isAuthorized) {
            getVendorData();
            socket.on('vendors', getVendorData);
        }

        return () => {
            socket.off('vendors');
        };
    }, [isConnected]);

    const columns = useMemo<GridColDef<VendorRow>[]>(
        () => [
            { field: 'col0', headerName: 'Vendor ID', flex: 0.15, minWidth: 45 },
            {   field: 'col1', 
                headerName: 'Is Payable', 
                flex: 0.15, 
                minWidth: 45,
                valueFormatter: (value) => {
                    if (value === true) {
                        return 'Yes'
                    } else {
                        return 'No'
                    }
                },
                filterOperators: [
                    {
                        label: 'Is Yes',
                        value: 'isYes',
                        getApplyFilterFn: (filterItem, column) => {
                            return (value, row, column, apiRef) => {
                             return value === true
                            }
                        },
                        getValueAsString: (params) => String(params.value)
                    },
                                        {
                        label: 'Is No',
                        value: 'isNo',
                        getApplyFilterFn: (filterItem, column) => {
                            return (value, row, column, apiRef) => {
                             return value === false
                            }
                        },
                        getValueAsString: (params) => String(params.value)
                    }
                ]
            },
            { field: 'col2', headerName: 'Non Pay Reason', flex: 0.15, minWidth: 100 },
            { field: 'col3', headerName: 'Company Name', flex: 0.25, minWidth: 100 },
            { field: 'col4', headerName: 'Email', flex: 0.25, minWidth: 120 },
            { field: 'col5', headerName: 'Division', flex: 0.1, minWidth: 150 },
            {
                field: 'col6',
                headerName: '% Over Revenue',
                flex: 0.1,
                minWidth: 150,
                editable: true
            },
            {
                field: 'col7',
                headerName: '% Over Profit',
                flex: 0.1,
                minWidth: 150,
                editable: true
            },
            {
                field: 'col8',
                headerName: 'Pay On',
                type: 'singleSelect',
                flex: 0.1,
                minWidth: 120,
                editable: true,
                valueOptions: [
                    { value: 1, label: 'Invoice Paid' },
                    { value: 2, label: 'Invoice Delivered' },
                    { value: 3, label: 'After Event' }
                ]
            },
            {
                field: 'col9',
                headerName: 'Should Calculate',
                type: 'singleSelect',
                flex: 0.12,
                minWidth: 100,
                editable: true,
                valueOptions: [
                    { value: true, label: 'Yes' },
                    { value: false, label: 'No' }
                ]
            },
            {
                field: 'col10',
                headerName: 'Actions',
                flex: 0.15,
                minWidth: 180,
                sortable: false,
                filterable: false,
                renderCell: (params) => {
                    const isDirty = isVendorRowDirty(params.row.id);

                    return (
                        <Box sx={{ display: 'flex', gap: 1, pt: 1.2 }}>
                            <Button
                                variant="contained"
                                color="error"
                                size="small"
                                disabled={!isDirty}
                                onClick={() =>
                                    handleSaveVendorUpdates(
                                        params.row.id,
                                        params.row
                                    )
                                }
                            >
                                Save
                            </Button>
                            <Button
                                variant="outlined"
                                size="small"
                                disabled={!isDirty}
                                onClick={() =>
                                    handleClearVendorRow(params.row.id)
                                }
                            >
                                Clear
                            </Button>
                        </Box>
                    );
                }
            }
        ],
        [editedVendorRows, originalVendorRows]
    );

    if (isAuthorized) 
        {
        return (
            <Box sx={{ p: 3 }}>
                <div style={{ height: '80vh', width: '100%' }}>
                    <DataGridPro
                        rows={vendorRows}
                        columns={columns}
                        loading={isLoading}
                        editMode="cell"
                        processRowUpdate={handleProcessVendorRowUpdate}
                        onProcessRowUpdateError={(error) => {
                            console.error(error);
                            toast.error('Failed to update row.');
                        }}
                        pagination
                        slots={{
                            toolbar: GridToolbar,
                            loadingOverlay: () => (
                                <Box
                                    sx={{
                                        height: '100%',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                    }}
                                >
                                    <CircularProgress />
                                </Box>
                            )
                        }}
                        slotProps={{
                            toolbar: {
                                showQuickFilter: true,
                                quickFilterProps: {
                                    debounceMs: 500
                                }
                            }
                        }}
                    />
                </div>
            </Box>
        );
    }
    return (
        <Box sx={{ textAlign: 'center', p: 3 }}>
            <WarningRoundedIcon />
            <p>Not Authorized</p>
        </Box>
    );
};

export default Vendors;